import { ref, nextTick } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import {
  DeleteShopDocument,
  DeleteShopMutation,
  DeleteShopMutationVariables,
  GetShopDocument,
  GetShopQuery,
  GetShopQueryVariables,
  ListDeliveryAreaQuery,
  ListDeliveryAreaDocument,
  ShopResponse,
} from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { routerPath } from "@/common/router";
import shopStore from "@/store/shopStore";
import searchConditionStore from "@/store/searchConditionStore";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import type { ListData, ShopListActionsParams } from "../types";
import { SHOP_LIST_FILE_NAME } from "../constants";

/**
 * 画面の操作イベントに関するハンドラをまとめて提供する composable。
 *
 * @returns 画面イベントから呼び出す操作ハンドラ群
 */
export const useShopListActions = (params: ShopListActionsParams & { getCurrentPage: () => number }) => {
  const {
    searchConditionChainCode,
    searchConditionShopCode,
    searchConditionPhone,
    searchConditionZipCode,
    searchConditionAddress,
    chainSelectValues,
    ShopNameSelectValues,
    chainViewFlag,
    errdialog,
    errMessage,
    editPage,
    confirmPage,
    overlay,
    confirmDeleteDialog,
    selectListCheckbox,
    searchQuery,
    openConfirmDeletePage,
  } = params;

  const router = useRouter();
  const store = useStore();

  // 店舗削除確認ダイアログ
  const displayConfirmDeleteDialog = ref(false);
  const deleteShop = ref<ShopResponse | null>(null);

  // 休日設定
  const holidaySetting = ref("");

  /**
   * 店舗編集アイコン @Click
   */
  const onClickEdit = async (shop_code: string): Promise<void> => {
    //編集ページ表示
    editPage.value = true;
    //検索条件を保持する
    searchConditionStore.setCondition({
      view_name: SHOP_LIST_FILE_NAME,
      conditions: new Map()
        .set("chain_code", searchConditionChainCode.value)
        .set("chain_name", chainSelectValues.value.name)
        .set("shop_code", searchConditionShopCode.value)
        .set("phone_number", searchConditionPhone.value)
        .set("zip_code", searchConditionZipCode.value)
        .set("address", searchConditionAddress.value)
        .set("page", String(params.getCurrentPage())),
    });

    // 店舗名を個別のMapエントリ（shop_code → name）として保存する
    const shopNameMap = new Map<string, string>();
    ShopNameSelectValues.value.forEach((item) => {
      shopNameMap.set(item.shop_code, item.name);
    });
    searchConditionStore.setCondition({
      view_name: "ShopName",
      conditions: shopNameMap,
    });
    // 「配達依頼登録-入力」画面へ遷移
    await shopStore.setupValue({
      chain_code: searchConditionChainCode.value
        ? searchConditionChainCode.value
        : localStorageGetChainCode(),
      shop_code: shop_code,
      shipping_label_print_flg: 0,
      delivery_order_print_flg: 0,
    });
    await router.push({ path: routerPath.shopRegister });
    store.commit("listEditOn");
  };

  /**
   * 一括削除 @Click
   */
  const onClickCheckDelete = async (): Promise<void> => {
    //削除可能かチェックする
    if (selectListCheckbox.value.length === 0) {
      return;
    }

    for (const val of selectListCheckbox.value) {
      const isDelete = await chkShopDelete(
        val["chain_code"],
        val["shop_code"],
        val["shop_name"]
      );
      if (!isDelete) {
        return;
      }
    }

    confirmDeleteDialog.value = true;
  };

  /**
   * 一括削除 実行
   */
  const onClickCheckDeleteComplete = async (): Promise<void> => {
    overlay.value = true;

    //1件ずつ削除する
    let result = false;
    const arrayError: string[] = [];
    for (const val of selectListCheckbox.value) {
      if (await deleteExecute(val["chain_code"], val["shop_code"])) {
        result = true;
      } else {
        arrayError.push(val["shop_code"]);
      }
    }

    //画面を閉じる
    confirmDeleteDialog.value = false;
    overlay.value = false;

    //画面遷移
    if (result) {
      await nextTick(async () => {
        //削除
        selectListCheckbox.value = [];
        // 絞り込み検索
        await searchQuery();
      });
    } else {
      errMessage.value =
        "店舗コード " + arrayError.join(",") + "の削除に失敗しました。";
      errdialog.value = true;
    }
  };

  /**
   * 店舗削除アイコン @Click
   */
  const onClickDelete = async (listData: ListData): Promise<void> => {
    //削除可能かチェックする
    const isDelete = await chkShopDelete(
      listData.chain_code,
      listData.shop_code,
      listData.shop_name
    );
    if (!isDelete) {
      return;
    }

    //graphql接続
    const apolloClient = await generateApolloClient();

    //店舗情報取得
    let result: ApolloQueryResult<GetShopQuery>;
    try {
      result = await apolloClient.query<GetShopQuery, GetShopQueryVariables>({
        query: GetShopDocument,
        variables: {
          chain_code: listData.chain_code,
          customer_code: localStorageGetCustomerCode(),
          shop_code: listData.shop_code,
        },
      });
    } catch (err) {
      console.error("err=", err);
      errdialog.value = true;
      errMessage.value = "店舗データの取得に失敗しました。";

      // 削除対象 店舗
      deleteShop.value = null;

      return;
    }

    if (result.data.getShop === undefined || result.data.getShop === null) {
      console.error("result.data.getShop is undefined or null");
      errdialog.value = true;
      errMessage.value = "店舗データの取得に失敗しました。";

      // 削除対象 店舗
      deleteShop.value = null;

      return;
    } else {
      // 削除対象 店舗
      deleteShop.value = result.data.getShop;
    }

    if (deleteShop.value == null) {
      return;
    }

    // 休日設定
    holidaySetting.value = "";
    if (deleteShop.value.holiday_mon == 1) {
      holidaySetting.value = "毎週月曜日";
    }
    if (deleteShop.value.holiday_tue == 1) {
      if (holidaySetting.value !== "") {
        holidaySetting.value += "、";
      }
      holidaySetting.value += "毎週火曜日";
    }
    if (deleteShop.value.holiday_wed == 1) {
      if (holidaySetting.value !== "") {
        holidaySetting.value += "、";
      }
      holidaySetting.value += "毎週水曜日";
    }
    if (deleteShop.value.holiday_thu == 1) {
      if (holidaySetting.value !== "") {
        holidaySetting.value += "、";
      }
      holidaySetting.value += "毎週木曜日";
    }
    if (deleteShop.value.holiday_fri == 1) {
      if (holidaySetting.value !== "") {
        holidaySetting.value += "、";
      }
      holidaySetting.value += "毎週金曜日";
    }
    if (deleteShop.value.holiday_sat == 1) {
      if (holidaySetting.value !== "") {
        holidaySetting.value += "、";
      }
      holidaySetting.value += "毎週土曜日";
    }
    if (deleteShop.value.holiday_sun == 1) {
      if (holidaySetting.value !== "") {
        holidaySetting.value += "、";
      }
      holidaySetting.value += "毎週日曜日";
    }

    // 確認ページを表示
    openConfirmDeletePage();
  };

  /**
   * エリア存在チェック
   */
  const chkShopDelete = async (
    chain_code: string,
    shop_code: string,
    shop_name: string
  ): Promise<boolean> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    try {
      //エリアが存在する店舗の削除不可
      const listShop: ApolloQueryResult<ListDeliveryAreaQuery> =
        await apolloClient.query<ListDeliveryAreaQuery>({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: chain_code,
            customer_code: localStorageGetCustomerCode(),
            shop_code: shop_code,
          },
        });

      if (
        listShop.data.listDeliveryArea !== null &&
        listShop.data.listDeliveryArea !== undefined &&
        listShop.data.listDeliveryArea.length > 0
      ) {
        errdialog.value = true;
        errMessage.value =
          shop_name +
          "にはエリアが登録されているため削除できません。エリアを削除してください。";
        return false;
      }
      return true;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "予期しないエラーが発生しました。";
      }
      return false;
    }
  };

  /**
   * 店舗削除確認キャンセル @Click
   */
  const onClickConfirmDeleteCancel = async (): Promise<void> => {
    displayConfirmDeleteDialog.value = false;
    editPage.value = false;
    confirmPage.value = false;
  };

  /**
   * 店舗削除確認OK @Click
   */
  const onClickConfirmDeleteOk = async (): Promise<void> => {
    if (deleteShop.value == null) {
      return;
    }

    //削除可能かチェックする
    const isDelete = await chkShopDelete(
      deleteShop.value.chain_code,
      deleteShop.value.shop_code,
      deleteShop.value.name
    );
    if (!isDelete) {
      return;
    }

    //編集ページ表示 OFF
    editPage.value = false;

    //確認ページ表示 OFF
    confirmPage.value = false;

    try {
      if (
        await deleteExecute(
          deleteShop.value.chain_code,
          deleteShop.value.shop_code
        )
      ) {
        await nextTick(async () => {
          // 絞り込み検索
          await searchQuery();
        });
      } else {
        errdialog.value = true;
        errMessage.value = "削除処理に失敗しました。";
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    }
  };

  /**
   * 削除処理GraphQL実行(1件ずつ)
   */
  const deleteExecute = async (
    chain_code: string,
    shop_code: string
  ): Promise<boolean> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 配達依頼削除
    try {
      await apolloClient.mutate<
        DeleteShopMutation,
        DeleteShopMutationVariables
      >({
        mutation: DeleteShopDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: chain_code,
          shop_code: shop_code,
          user_id: localStorageGetUserId(),
        },
      });
      return true;
    } catch (err: unknown) {
      console.error("err=", err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "店舗の削除に失敗しました。";
      } else {
        errMessage.value = "店舗の削除に失敗しました。（不明なエラー）";
      }
      return false;
    }
  };

  return {
    displayConfirmDeleteDialog,
    deleteShop,
    holidaySetting,
    onClickEdit,
    onClickCheckDelete,
    onClickCheckDeleteComplete,
    onClickDelete,
    onClickConfirmDeleteCancel,
    onClickConfirmDeleteOk,
  };
};
